import React, { useEffect, useState } from 'react';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Send, Landmark, AlertTriangle } from 'lucide-react';

const authHeaders = () => {
  const h = { 'Content-Type': 'application/json' };
  const t = localStorage.getItem('session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
};

export const RazorpayPayoutDialog = ({ open, onClose, vendors = [], tours = [], prefill = {}, onCreated }) => {
  const [form, setForm] = useState({});
  const [account, setAccount] = useState(null);
  const [acctForm, setAcctForm] = useState({ account_type: 'bank_account' });
  const [prefilled, setPrefilled] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setForm({ ...prefill }); setAccount(null); setAcctForm({ account_type: 'bank_account' }); setPrefilled(false); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.vendor_id) { setAccount(null); return; }
    fetch(`${API}/razorpay/vendors/${form.vendor_id}/payout-account`, { credentials: 'include', headers: authHeaders() })
      .then((r) => r.json())
      .then((data) => {
        setAccount(data);
        const p = data?.prefill || {};
        const hasBank = !!(p.account_number && p.ifsc);
        const hasAny = hasBank || !!p.vpa || !!p.beneficiary_name;
        setPrefilled(!data.configured && hasAny);
        if (!data.configured) {
          setAcctForm({
            account_type: hasBank ? 'bank_account' : (p.vpa ? 'vpa' : 'bank_account'),
            beneficiary_name: p.beneficiary_name || '',
            account_number: p.account_number || '',
            ifsc: p.ifsc || '',
            vpa: p.vpa || '',
          });
        }
      })
      .catch(() => setAccount(null));
  }, [form.vendor_id]);

  const saveAccount = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/razorpay/vendors/${form.vendor_id}/payout-account`, {
        method: 'POST', credentials: 'include', headers: authHeaders(), body: JSON.stringify(acctForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setAccount((a) => ({ ...(a || {}), ...data }));
      if (data.configured) toast.success('Payout account saved & verified with RazorpayX');
      else if (data.saved_locally) toast.warning('Details saved — RazorpayX registration pending (see note below)', { duration: 7000 });
    } catch (err) { toast.error(err.message); }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/razorpay/payouts`, {
        method: 'POST', credentials: 'include', headers: authHeaders(),
        body: JSON.stringify({
          vendor_id: form.vendor_id, amount: parseFloat(form.amount) || 0,
          tour_id: form.tour_id || '', notes: form.notes || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success(`Payout ${data.status} — ₹${data.amount.toLocaleString('en-IN')} to ${data.vendor_name}`);
      onCreated && onCreated(data);
      onClose();
    } catch (err) { toast.error(err.message, { duration: 12000 }); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="razorpay-payout-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Landmark className="w-4 h-4" />Pay Vendor (RazorpayX)</DialogTitle>
          <DialogDescription className="sr-only">Send a RazorpayX payout to a vendor</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div><Label>Vendor *</Label>
            <Select value={form.vendor_id || ''} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
              <SelectTrigger data-testid="payout-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}{v.type ? ` · ${v.type}` : ''}</SelectItem>)}</SelectContent>
            </Select></div>

          {form.vendor_id && account && !account.configured && (
            <div className="space-y-2 p-3 rounded-lg border bg-muted/30">
              <p className="text-xs text-muted-foreground">
                {account.saved_locally ? 'Saved payout details (editable):' : 'No payout account saved for this vendor yet — add one:'}
                {prefilled && !account.saved_locally && <span className="text-emerald-700 font-medium"> prefilled from vendor records</span>}
              </p>
              <Select value={acctForm.account_type} onValueChange={(v) => setAcctForm({ ...acctForm, account_type: v })}>
                <SelectTrigger data-testid="payout-account-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_account">Bank account</SelectItem>
                  <SelectItem value="vpa">UPI ID</SelectItem>
                </SelectContent>
              </Select>
              {acctForm.account_type === 'vpa' ? (
                <Input data-testid="payout-vpa-input" placeholder="vendor@upi" value={acctForm.vpa || ''} onChange={(e) => setAcctForm({ ...acctForm, vpa: e.target.value })} />
              ) : (
                <>
                  <Input data-testid="payout-benef-input" placeholder="Beneficiary name" value={acctForm.beneficiary_name || ''} onChange={(e) => setAcctForm({ ...acctForm, beneficiary_name: e.target.value })} />
                  <Input data-testid="payout-acct-input" placeholder="Account number" value={acctForm.account_number || ''} onChange={(e) => setAcctForm({ ...acctForm, account_number: e.target.value })} />
                  <Input data-testid="payout-ifsc-input" placeholder="IFSC" value={acctForm.ifsc || ''} onChange={(e) => setAcctForm({ ...acctForm, ifsc: e.target.value })} />
                </>
              )}
              <Button type="button" size="sm" onClick={saveAccount} disabled={busy} data-testid="payout-save-account-btn">
                {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}Save account</Button>
              {account.razorpay_error && (
                <div className="p-2 rounded-md bg-amber-50 border border-amber-200" data-testid="payout-rzp-error">
                  <p className="text-[11px] text-amber-900 font-medium flex items-start gap-1"><AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />Details saved locally, but RazorpayX refused: {account.razorpay_error}</p>
                  {account.hint && <p className="text-[10px] text-amber-800 mt-1">{account.hint}</p>}
                </div>
              )}
            </div>
          )}
          {form.vendor_id && account?.configured && (
            <p className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2" data-testid="payout-account-label">Paying to: {account.label}</p>
          )}

          <div><Label>Amount (₹) *</Label><Input data-testid="payout-amount-input" type="number" step="0.01" min="1" value={form.amount || ''} onChange={(e) => setForm({ ...form, amount: e.target.value })} required /></div>
          {tours.length > 0 && (
            <div><Label>Tour</Label>
              <Select value={form.tour_id || ''} onValueChange={(v) => setForm({ ...form, tour_id: v })}>
                <SelectTrigger><SelectValue placeholder="Link to a tour (optional)" /></SelectTrigger>
                <SelectContent>{tours.map((t) => <SelectItem key={t.id} value={t.id}>{t.tour_name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div><Label>Notes</Label><Input value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || !form.vendor_id || !(account?.configured || account?.saved_locally)} data-testid="payout-send-btn">
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}Send Payout</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RazorpayPayoutDialog;
